import React from 'react';

// ============================================================================
// InlineNotice — small inline error/warning banner used in place of alert()
// ============================================================================
// Drop in next to a form action area (inside a modal) or above a list view
// (after a filter row). Renders nothing when `notice` is null. Caller owns
// the state so the same notice object can be rendered in two places (e.g.
// inside an open modal AND on the list view behind it) and dismissed once.
//
// Shape:
//   notice = { kind: 'error' | 'warning' | 'success' | 'info', message: string } | null
//
// `kind` defaults to 'error'. Multi-line messages are honored via
// white-space: pre-line on the body text. The 'success' kind uses a
// green palette and is intended for short confirmation messages where
// a banner is more visible than relying on state to re-render
// (e.g. an admin "Synced" confirmation). The 'info' kind uses a blue
// palette for benign, non-error notices (e.g. "No changes to save.") so
// they do not render with alarming error styling.
// ============================================================================

// `message` is nullable because the canonical boot call site builds the notice
// as {kind: 'error', message: bootError} where bootError is null until a load
// actually fails; the component's own first line is what turns that into a
// no-render.
export type InlineNoticeKind = 'error' | 'warning' | 'success' | 'info';

export type Notice = {
  kind?: InlineNoticeKind;
  message?: string | null;
};

type InlineNoticeProps = {
  notice?: Notice | null;
  onDismiss?: React.MouseEventHandler<HTMLButtonElement>;
  // F04: the error banner carries its own secondary Retry inside it.
  action?: React.ReactNode;
};

export default function InlineNotice({notice, onDismiss, action}: InlineNoticeProps) {
  if (!notice || !notice.message) return null;
  let kind;
  if (notice.kind === 'warning') kind = 'warning';
  else if (notice.kind === 'success') kind = 'success';
  else if (notice.kind === 'info') kind = 'info';
  else kind = 'error';
  // The one Notice in four tones (F03): the soft background with its Table A
  // ink, from the Design Constitution tokens. error = danger, warning = warn,
  // success = ok. No orange, and plain help is gray text, not a notice.
  const palette =
    kind === 'warning'
      ? {bg: 'var(--warn-soft)', fg: 'var(--warn-ink)'}
      : kind === 'success'
        ? {bg: 'var(--ok-soft)', fg: 'var(--ok-ink)'}
        : kind === 'info'
          ? {bg: 'var(--info-soft)', fg: 'var(--info-ink)'}
          : {bg: 'var(--danger-soft)', fg: 'var(--danger-ink)'};
  return (
    <div
      role="alert"
      data-inline-notice={kind}
      style={{
        background: palette.bg,
        color: palette.fg,
        borderRadius: 12,
        padding: '10px 14px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {/* Review F15: centre the message vertically in the row, matching the
          action - with a single-line message and a tall button the old
          flex-start left the text pinned to the top. Multi-line messages are
          the tallest element in the row, so this changes nothing for them. */}
      <div style={{flex: 1, alignSelf: 'center', whiteSpace: 'pre-line', minWidth: 0, wordBreak: 'break-word'}}>
        {notice.message}
      </div>
      {action ? (
        <div data-inline-notice-action="1" style={{flex: 'none', alignSelf: 'center'}}>
          {action}
        </div>
      ) : null}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notice"
          style={{
            background: 'none',
            border: 'none',
            color: palette.fg,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
            marginTop: 2,
            fontFamily: 'inherit',
          }}
        >
          {'×'}
        </button>
      )}
    </div>
  );
}
